import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDashboardStore } from './useDashboardStore';
import { cn } from '@/lib/cn';
import {
  HomeIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  CurrencyEuroIcon,
  UserGroupIcon,
  UsersIcon,
  BuildingLibraryIcon,
  FolderIcon,
  ChartBarIcon,
  GiftIcon,
  RocketLaunchIcon,
  CpuChipIcon,
  BuildingOffice2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  LifebuoyIcon,
  ShieldCheckIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useCallback } from 'react';
import { useSubscription } from '@/hooks/useApi';
import { useAuthStore, isSuperAdmin } from '@/stores/authStore';

// ── Nav items (shared with shortcuts) ─────────────────────────
export const NAV_ITEMS = [
  { path: '/', icon: HomeIcon, label: 'nav.dashboard', color: 'bg-primary-600' },
  { path: '/invoices', icon: DocumentTextIcon, label: 'nav.invoices', color: 'bg-blue-500' },
  { path: '/quotes', icon: DocumentDuplicateIcon, label: 'nav.quotes', color: 'bg-violet-500' },
  { path: '/expenses', icon: CurrencyEuroIcon, label: 'nav.expenses', color: 'bg-amber-500' },
  { path: '/clients', icon: UserGroupIcon, label: 'nav.clients', color: 'bg-emerald-500' },
  { path: '/bank', icon: BuildingLibraryIcon, label: 'nav.bank', color: 'bg-cyan-500' },
  { path: '/documents', icon: FolderIcon, label: 'nav.documents', color: 'bg-orange-500' },
  { path: '/reports/vat', icon: ChartBarIcon, label: 'nav.vatReports', color: 'bg-rose-500' },
  { path: '/team', icon: UsersIcon, label: 'nav.team', color: 'bg-indigo-500' },
  { path: '/settings/api-keys', icon: KeyIcon, label: 'nav.apiKeys', color: 'bg-fuchsia-500' },
  { path: '/support', icon: LifebuoyIcon, label: 'nav.support', color: 'bg-teal-500' },
] as const;

// ── Mobile overlay state (local, not persisted) ──────────────
import { create } from 'zustand';

interface MobileSidebarState {
  open: boolean;
  setOpen: (v: boolean) => void;
}

export const useMobileSidebar = create<MobileSidebarState>()((set) => ({
  open: false,
  setOpen: (v) => set({ open: v }),
}));

// ── Component ─────────────────────────────────────────────────
export default function Sidebar() {
  const { t } = useTranslation();
  const expanded = useDashboardStore((s) => s.sidebarExpanded);
  const toggleSidebar = useDashboardStore((s) => s.toggleSidebar);
  const setSidebarExpanded = useDashboardStore((s) => s.setSidebarExpanded);
  const mobileOpen = useMobileSidebar((s) => s.open);
  const setMobileOpen = useMobileSidebar((s) => s.setOpen);
  const { data: subscription } = useSubscription();
  const user = useAuthStore((s) => s.user);
  const showAdmin = isSuperAdmin(user);

  const planId = subscription?.plan ?? 'free';
  const planMeta: Record<string, {
    icon: typeof GiftIcon;
    desktopIconBg: string;
    desktopIconColor: string;
    mobileIconBg: string;
    mobileIconColor: string;
  }> = {
    free: {
      icon: GiftIcon,
      desktopIconBg: 'bg-emerald-400/20',
      desktopIconColor: 'text-emerald-100',
      mobileIconBg: 'bg-emerald-100',
      mobileIconColor: 'text-emerald-600',
    },
    starter: {
      icon: RocketLaunchIcon,
      desktopIconBg: 'bg-sky-400/20',
      desktopIconColor: 'text-sky-100',
      mobileIconBg: 'bg-sky-100',
      mobileIconColor: 'text-sky-600',
    },
    professional: {
      icon: CpuChipIcon,
      desktopIconBg: 'bg-violet-400/20',
      desktopIconColor: 'text-violet-100',
      mobileIconBg: 'bg-violet-100',
      mobileIconColor: 'text-violet-600',
    },
    enterprise: {
      icon: BuildingOffice2Icon,
      desktopIconBg: 'bg-amber-400/20',
      desktopIconColor: 'text-amber-100',
      mobileIconBg: 'bg-amber-100',
      mobileIconColor: 'text-amber-700',
    },
  };
  const currentPlan = planMeta[planId] ?? planMeta.free;
  const PlanIcon = currentPlan.icon;

  // Auto-collapse on small screens
  const handleResize = useCallback(() => {
    if (window.innerWidth < 768) {
      setSidebarExpanded(false);
    }
  }, [setSidebarExpanded]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // ── Desktop nav content ─────────────────────────────────────
  const desktopNavContent = (
    <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="navigation">
      {NAV_ITEMS.map(({ path, icon: Icon, label }) => (
        <NavLink
          key={path}
          to={path}
          end={path === '/'}
          className={({ isActive }) =>
            cn(
              'group flex items-center gap-3 rounded-lg text-sm font-medium',
              'transition-all duration-200 ease-in-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-700',
              expanded ? 'px-3 py-2.5' : 'justify-center px-2 py-2.5',
              isActive
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-primary-100 hover:bg-primary-800 hover:text-white',
            )
          }
        >
          <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
          {expanded && (
            <span className="truncate">{t(label)}</span>
          )}
        </NavLink>
      ))}

      {showAdmin && (
        <NavLink
          to="/admin"
          className={({ isActive }) =>
            cn(
              'group flex items-center gap-3 rounded-lg text-sm font-medium',
              'transition-all duration-200 ease-in-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-700',
              expanded ? 'px-3 py-2.5' : 'justify-center px-2 py-2.5',
              isActive
                ? 'bg-amber-500 text-white shadow-sm'
                : 'text-amber-200 hover:bg-primary-800 hover:text-white',
            )
          }
          title={expanded ? undefined : t('admin.console')}
        >
          <ShieldCheckIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          {expanded && (
            <span className="truncate">{t('admin.console')}</span>
          )}
        </NavLink>
      )}

      {/* Subscription quick link at the end of menu */}
      <div className="mt-2 pt-2 border-t border-primary-600/40">
        <NavLink
          to="/subscription"
          className={({ isActive }) =>
            cn(
              'group flex items-center gap-3 rounded-lg text-sm font-medium',
              'transition-all duration-200 ease-in-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-700',
              expanded ? 'px-3 py-2.5' : 'justify-center px-2 py-2.5',
              isActive
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-primary-100 hover:bg-primary-800 hover:text-white',
            )
          }
          title={expanded ? undefined : t('nav.subscription')}
        >
          <span
            className={cn(
              'h-5 w-5 rounded-md flex items-center justify-center shrink-0',
              currentPlan.desktopIconBg,
            )}
            aria-hidden="true"
          >
            <PlanIcon className={cn('h-3.5 w-3.5', currentPlan.desktopIconColor)} />
          </span>
          {expanded && (
            <div className="min-w-0">
              <span className="block truncate">{t('nav.subscription')}</span>
              <span className="block text-[11px] text-primary-200 capitalize -mt-0.5 truncate">
                {t(`subscription.plans.${planId}`)}
              </span>
            </div>
          )}
        </NavLink>
      </div>
    </nav>
  );

  const sidebarInner = (
    <div className="flex h-full flex-col bg-primary-700">
      {/* Header */}
      <div
        className={cn(
          'flex items-center border-b border-primary-600/40 h-16 shrink-0',
          expanded ? 'justify-between px-4' : 'justify-center px-2',
        )}
      >
        {expanded ? (
          <>
            <div className="flex items-center gap-2.5">
              <img src="/logo.svg" alt="Ledgora" className="h-8 w-8" />
              <span className="text-sm font-semibold text-white tracking-tight">
                {import.meta.env.VITE_APP_NAME}
              </span>
            </div>
            <button
              onClick={toggleSidebar}
              className={cn(
                'hidden md:flex h-7 w-7 items-center justify-center rounded-md',
                'text-primary-200 hover:text-white hover:bg-primary-800',
                'transition-all duration-200 ease-in-out',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
              )}
              aria-label="Collapse sidebar"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
          </>
        ) : (
          <button
            onClick={toggleSidebar}
            className={cn(
              'hidden md:flex h-8 w-8 items-center justify-center rounded-md',
              'text-primary-200 hover:text-white hover:bg-primary-800',
              'transition-all duration-200 ease-in-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
            )}
            aria-label="Expand sidebar"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {desktopNavContent}


    </div>
  );

  return (
    <>
      {/* ── Mobile full-screen drawer ──────────────────────── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />

          {/* Slide-in panel */}
          <aside
            className="absolute inset-y-0 left-0 flex w-[85vw] max-w-xs flex-col bg-white shadow-2xl animate-slide-in-left"
            role="complementary"
            aria-label="Navigation"
          >
            {/* Mobile header */}
            <div className="flex items-center justify-between px-5 h-16 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2.5">
                <img src="/logo.svg" alt="Ledgora" className="h-9 w-9" />
                <span className="text-base font-semibold text-gray-900 tracking-tight">
                  {import.meta.env.VITE_APP_NAME}
                </span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                aria-label="Close menu"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Mobile navigation — big, touch-friendly rows */}
            <nav className="flex-1 overflow-y-auto px-3 py-3" role="navigation">
              <div className="space-y-1">
                {NAV_ITEMS.map(({ path, icon: Icon, label, color }) => (
                  <NavLink
                    key={path}
                    to={path}
                    end={path === '/'}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-4 rounded-xl px-4 py-3.5',
                        'text-[15px] font-medium transition-all duration-150',
                        'active:scale-[0.98]',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100',
                      )
                    }
                  >
                    <div
                      className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-xl shrink-0',
                        color,
                        'text-white shadow-sm',
                      )}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <span>{t(label)}</span>
                  </NavLink>
                ))}

                {showAdmin && (
                  <NavLink
                    to="/admin"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-4 rounded-xl px-4 py-3.5',
                        'text-[15px] font-medium transition-all duration-150',
                        'active:scale-[0.98]',
                        isActive
                          ? 'bg-amber-50 text-amber-700'
                          : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100',
                      )
                    }
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl shrink-0 bg-amber-500 text-white shadow-sm">
                      <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <span>{t('admin.console')}</span>
                  </NavLink>
                )}

                <div className="mt-2 pt-2 border-t border-gray-100">
                  <NavLink
                    to="/subscription"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-4 rounded-xl px-4 py-3.5',
                        'text-[15px] font-medium transition-all duration-150',
                        'active:scale-[0.98]',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100',
                      )
                    }
                  >
                    <div
                      className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-xl shrink-0',
                        currentPlan.mobileIconBg,
                      )}
                    >
                      <PlanIcon className={cn('h-5 w-5', currentPlan.mobileIconColor)} aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <span className="block">{t('nav.subscription')}</span>
                      <span className="block text-xs text-gray-500 capitalize truncate">
                        {t(`subscription.plans.${planId}`)}
                      </span>
                    </div>
                  </NavLink>
                </div>
              </div>
            </nav>

            {/* Mobile footer */}
            <div
              className="border-t border-gray-100 px-3 py-3"
              style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
            >
              {/* Mobile plan badge */}

            </div>
          </aside>
        </div>
      )}

      {/* ── Desktop sidebar ────────────────────────────────── */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 hidden md:flex flex-col',
          'transition-all duration-200 ease-in-out',
          expanded ? 'w-64' : 'w-16',
        )}
        role="complementary"
        aria-label="Sidebar navigation"
      >
        {sidebarInner}
      </aside>
    </>
  );
}
